import { Loader2, CloudOff } from 'lucide-react';
import type { Translate } from '@/i18n/translator';
import { useTranslations } from '@/i18n/use-locale';
import { isApiError } from '@/lib/unwrap';

import { Button } from './button';
import { EmptyState } from './empty-state';

/**
 * The waiting state for a whole route while its first fetch is in flight.
 *
 * This exists so a page never has to fall back to seeded mock data to have
 * something to show on load. A spinner is honest about "not here yet";
 * fabricated rows that vanish a second later are not — that flash was the whole
 * complaint this replaces.
 */
export function RouteLoading() {
  const t = useTranslations('admin.routeState');

  return (
    <div
      role="status"
      aria-label={t('loading')}
      className="grid min-h-[240px] place-items-center py-16"
    >
      <Loader2 className="h-7 w-7 animate-spin text-pitch" strokeWidth={2.4} aria-hidden />
    </div>
  );
}

/**
 * The failed state for a whole route, with the one action that helps: retry.
 *
 * Shown instead of the dummy fallback on a failed load. An admin making
 * decisions off fabricated numbers is strictly worse than an honest "couldn't
 * load" — especially on a console where an endpoint returning 422 is a bug we
 * want visible, not papered over with plausible-looking mock data.
 *
 * It reads the query's own `error`, so the hint can say what the server said — a 403
 * is a different conversation from a dropped connection — and it takes `refetch` as it
 * comes, promise and all: `<RouteError error={q.error} onRetry={q.refetch} />`.
 */
export function RouteError({ error, onRetry }: { error?: unknown; onRetry: () => unknown }) {
  const t = useTranslations('admin.routeState');

  return (
    <EmptyState
      icon={CloudOff}
      title={t('errorTitle')}
      hint={describeFailure(error, t)}
      action={
        <Button type="button" onClick={() => void onRetry()} className="py-2">
          {t('retry')}
        </Button>
      }
    />
  );
}

/** One sentence on what went wrong: the status when there was one, the generic hint otherwise. */
function describeFailure(error: unknown, t: Translate): string {
  if (isApiError(error) && error.status !== undefined) {
    return error.status === 403 ? t('errorForbidden') : t('errorStatus', { status: error.status });
  }
  return t('errorHint');
}
